import { motion } from "framer-motion";
import { PenLine, Share2, Globe, MapPin } from "lucide-react";

const services = [
  {
    n: "01",
    icon: PenLine,
    title: "Content Creation",
    desc: "Scroll-stopping reels, carousels & patient education that actually gets saved and shared.",
    tag: "Formulation",
  },
  {
    n: "02",
    icon: Share2,
    title: "Social Media",
    desc: "Full management across Instagram, LinkedIn & Facebook. We post, engage, grow — you treat patients.",
    tag: "Daily Dosage",
  },
  {
    n: "03",
    icon: Globe,
    title: "Website Design",
    desc: "Fast, trustworthy, conversion-first websites that turn curious visitors into booked appointments.",
    tag: "Vital Signs",
  },
  {
    n: "04",
    icon: MapPin,
    title: "Google My Business",
    desc: "Own the local map pack. Reviews, listings & SEO so patients near you find you first.",
    tag: "Location Rx",
  },
];

const container = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };
const card = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

export default function Services() {
  return (
    <section id="services" className="bg-white py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="mb-16 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-blue-deep">/ Inside the lab</span>
            <h2 className="mt-4 max-w-2xl font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-obsidian md:text-5xl">
              Four compounds. One healthy growth curve.
            </h2>
          </div>
          <p className="max-w-sm text-slate-600">
            Everything a modern practice needs to look credible, stay visible, and fill the calendar — plus websites that can be found by AI.
          </p>
        </div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-1 gap-px overflow-hidden rounded-3xl border border-slate-200 bg-slate-200 md:grid-cols-2 lg:grid-cols-4"
        >
          {services.map((s) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.n}
                variants={card}
                data-testid={`service-card-${s.n}`}
                className="group relative flex flex-col justify-between bg-white p-8 transition-colors duration-500 hover:bg-slate-50"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-slate-400">{s.n}</span>
                    <span className="rounded-full bg-blue-light px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-blue-deep">
                      {s.tag}
                    </span>
                  </div>
                  <div className="mt-8 inline-flex rounded-2xl border border-slate-200 p-3 transition-colors duration-500 group-hover:border-lab-green group-hover:bg-lab-light">
                    <Icon className="h-7 w-7 text-blue-deep transition-colors duration-500 group-hover:text-lab-deep" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-bold text-obsidian">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                </div>
                <div className="mt-8 h-1 w-12 rounded-full bg-lab-green transition-all duration-500 group-hover:w-full" />
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
